import { watch, onMounted, ref, reactive, computed } from 'vue';
import { userStore } from '@/store/user-store';
import { projectStore } from '@/store/project-store';
import { thoughtStore } from '@/store/thought-store';
import { IComment } from '@/models/comment.model';
import { IThought, IThoughtFeedbackResponse } from '@/models/thought.model';
import useConverter from '@/composables/general/useConverter';
import useLocalizeMessages from '@/composables/general/useLocalizeMessages';
import { RepositoryFactory } from '@/api/repositoryFactory';
const ThoughtRepository = RepositoryFactory.get('thought');
const FeedbackRepository = RepositoryFactory.get('feedback');

interface CommentLists {
  normal: IComment[];
  highlighted: IComment[];
  max: number;
}

interface ThoughtUserResponseState {
  exists: boolean;
  response: IThoughtFeedbackResponse | null;
  errors: any;
  success: string;
  submitting: boolean;
  refreshing: boolean;
}

interface ThoughtConfig {
  showThumbnail: boolean | false;
  hideVoting: boolean | false;
  hideComments: boolean | false;
  hideCommentButton: boolean | false;
}

interface AnObject {
  [key: string]: any;
}

// used inf components/discuss/comment/comments
// list of comments in thought context
// TODO use this for vote-info also
export default function useThoughtFeedbackState(thoughtid: number, config: ThoughtConfig) {
  // -- VAR INITIALIZATION --
  const visibleIfResult = ref({}) as AnObject;
  const requiredIfResult = ref({}) as AnObject;

  const { toBool } = useConverter();
  const { l } = useLocalizeMessages();

  // const thought = reactive(null);

  const feedbackState = reactive({
    exists: false,
    response: null,
    errors: {},
    success: '',
    submitting: false,
    refreshing: true,
  }) as ThoughtUserResponseState;

  // -- COMPUTED ---
  const thought = computed(() => {
    return thoughtStore.getState().thoughts.find(t => t.id == thoughtid);
  });
  const topicid = computed(() => {
    return thought.value ? thought.value.topic.id : null;
  });
  const topic = computed(() => {
    if (topicid.value) {
      return projectStore.getFetchedTopic(topicid.value);
    }
    return null;
  });
  const feedbackAttributes = computed(() => {
    return topic.value?.feedback?.attributes;
  });
  const user = computed(() => {
    return userStore.getState().user;
  });
  const isLoggedIn = computed(() => {
    return userStore.isLoggedIn();
  });
  const isOwnThought = computed(() => {
    if (thought.value && thought.value.owner) {
      return user.value && user.value.id == thought.value.owner.id;
    }
    return false;
  });

  function resetFeedbackState() {
    feedbackState.exists = false;
    feedbackState.response = null;
    feedbackState.errors = {};
    feedbackState.success = '';
    feedbackState.submitting = false;
    feedbackState.refreshing = false;
  }

  function refreshResponseState() {
    resetFeedbackState();
    feedbackState.refreshing = true;
    if (thought.value && user.value) {
      FeedbackRepository.getForUser(thought.value.id, user.value.id)
        .then((response: IThoughtFeedbackResponse[]) => {
          // console.log("fetching response of user")
          // console.log(response)
          if (response?.length > 0) {
            feedbackState.response = response[0];
            feedbackState.exists = true;
          }
        })
        .finally(() => {
          feedbackState.refreshing = false;
        });
    }
  }

  function displayServerErrors(error: any) {
    try {
      const errData = error.response.data;
      feedbackState.errors['all'] = l(errData.message);
      // If there are attribute errors, we try to display the attribute's default error message
      // Although this is rarely executed since attribute checking is also done before submitting
      if (errData.attribute_ids) {
        const fdbkAttrs = feedbackAttributes.value;
        errData.attribute_ids.forEach((id: number) => {
          const existing = fdbkAttrs.filter((a: any) => a.id == id);
          if (existing.length > 0) {
            feedbackState.errors['attribute-' + id] = existing[0].error;
          }
        });
      }
    } catch (e) {
      feedbackState.errors['all'] = 'Fehler: ' + e;
    }
  }
  function submitFeedbackResponse(values: any) {
    feedbackState.errors = {};
    feedbackState.success = '';
    feedbackState.submitting = true;

    return new Promise((resolve, reject) => {
      if (feedbackState.response == null) {
        // Post new feedback
        FeedbackRepository.post(thought.value?.id, values)
          .then((response: IThoughtFeedbackResponse) => {
            // console.log(response);
            // emit that feedback was successfully created
            if (response.id) {
              feedbackState.response = response;
              feedbackState.success = 'Feedback erfolgreich abgesendet';
            }
          })
          .catch((error: string) => {
            console.log(error);
            displayServerErrors(error);
          })
          .finally(() => {
            feedbackState.submitting = false;
            resolve(true);
          });
      } else {
        // Edit mode. PUT edit
        FeedbackRepository.put(feedbackState.response.id, thought.value?.id, values)
          .then((response: IThoughtFeedbackResponse) => {
            // console.log('put');
            // console.log(response);
            feedbackState.response = response;
            if (response.id) {
              feedbackState.response = response;
              feedbackState.success = 'Feedback erfolgreich abgesendet';
            }
          })
          .catch((error: string) => {
            console.log(error);
            displayServerErrors(error);
          })
          .finally(() => {
            feedbackState.submitting = false;
            resolve(true);
          });
      }
    });
  }

  function initializeFeedbackState() {
    feedbackAttributes.value.forEach((attr: any) => {
      requiredIfResult.value[attr.code] = toBool(attr.required);
    });

    if (thought.value && user.value) {
      refreshResponseState();
    }
  }

  watch(
    () => userStore.getState().user,
    (newUser, oldUser) => {
      if ((newUser == null && oldUser) || (newUser && oldUser == null) || newUser?.id != oldUser?.id) {
        refreshResponseState();
      }
    }
  );

  // initial setup for comments
  // check if highlighted or "my" comment are available to display as teaser
  // if not show any normal comment
  onMounted(() => {
    if (feedbackAttributes.value) {
      initializeFeedbackState();
    }
  });

  return {
    visibleIfResult,
    requiredIfResult,
    feedbackState,
    feedbackAttributes,
    refreshResponseState,
    submitFeedbackResponse,
    initializeFeedbackState,
  };
}
